import { CrudLink } from "@/shared/components";
import type { DataListViewColumns } from "@/shared/components/DataListView";
import { Stack, Typography } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import type { ColumnDefinition } from "@/shared/components/CRUD/types";
import type { PlatformRow } from "./types";

/**
 * Конфигурация UI для feature управления платформами
 * Адаптировано из Arenas.js
 */

// Статусы платформ
const statuses: Record<string, string> = {
  enabled: "Включена",
  draft: "Черновик",
  disabled: "Отключена",
};

// Конфигурация колонок таблицы
export const getColumnsConfig = (): ColumnDefinition<PlatformRow>[] => [
  {
    name: "Title",
    label: "Название",
    options: {
      customBodyRender: (value: unknown) => {
        const [title, id] = value as [string, string];
        return (
          <CrudLink tab="details" id={id}>
            {title}
          </CrudLink>
        );
      },
    },
  },
  {
    name: "Status",
    label: "Статус",
    options: {
      customBodyRender: (value: unknown) => {
        const [status] = value as [string];
        return (
          <Chip
            size="small"
            color={status === "enabled" ? "success" : "default"}
            label={statuses[status] || status}
          />
        );
      },
    },
  },
  {
    name: "Published",
    label: "Опубликована",
    options: {
      customBodyRender: (value: unknown) => {
        const [published] = value as [boolean];
        return (
          <Chip
            size="small"
            color={published ? "success" : "default"}
            label={published ? "Да" : "Нет"}
          />
        );
      },
    },
  },
  {
    name: "Logo",
    label: "Логотип",
    options: {
      customBodyRender: (value: unknown) => {
        const [title, logo] = value as [string, { url: string } | null];
        return logo ? (
          <img
            src={logo.url}
            style={{ maxWidth: 100, maxHeight: 100 }}
            alt=""
          />
        ) : (
          <Avatar>{title?.[0] || "P"}</Avatar>
        );
      },
    },
  },
  {
    name: "Host",
    label: "Хост",
    options: {
      customBodyRender: (value: unknown) => {
        // Как «Tech link» у арен в референсе: сверху основной домен, если он
        // подключён, снизу всегда служебный slug.<base>, по которому витрина
        // открывается даже до подключения домена и до публикации.
        const [host, slugHost, slugUrl, url] = value as [
          string | null,
          string,
          string,
          string,
        ];
        return (
          <>
            {host && (
              <div>
                <a href={url} target="_blank" rel="noreferrer noopener">
                  {host}
                </a>
              </div>
            )}
            <Typography
              component="a"
              href={slugUrl}
              target="_blank"
              rel="noreferrer noopener"
              variant="caption"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              {slugHost}
            </Typography>
          </>
        );
      },
    },
  },
];

// Конфигурация текстов интерфейса
export const getTextsConfig = () => ({
  filterTitle: "Фильтры",
  listTitle: "Платформы",
  itemCreated: "Платформа создана",
  itemUpdated: "Платформа обновлена",
  createItem: "Создать платформу",
  refetchList: "Обновить список",
  deleteItems: "Удалить платформы",
  itemsDeleted: "Платформы удалены",
  restoreItems: "Восстановить платформы",
  itemsRestored: "Платформы восстановлены",
  cancel: "Отмена",
  editItem: "Платформа",
  showTitle: "Детали",
});

// Колонки для мобильного представления: название с хостами, статус и публикация
export const getMobileColumnsConfig = (): DataListViewColumns<PlatformRow> => ({
  main: {
    title: "Платформа",
    sx: { flex: 1, minWidth: 0 },
    renderCell: (row) => (
      <Stack sx={{ minWidth: 0 }}>
        <CrudLink id={row.id} tab="details" noWrap>
          {row.title}
        </CrudLink>
        {row.host && (
          <Typography variant="body2" color="textSecondary" noWrap>
            {row.host}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" noWrap>
          {row.slugHost}
        </Typography>
      </Stack>
    ),
  },
  status: {
    title: "Статус",
    sx: { width: 96, flexShrink: 0 },
    renderCell: (row) => (
      <Chip
        size="small"
        color={row.status === "enabled" ? "success" : "default"}
        label={statuses[row.status as string] || row.status}
      />
    ),
  },
  published: {
    title: "Опубликована",
    sx: { width: 116, flexShrink: 0 },
    renderCell: (row) => (
      <Chip
        size="small"
        color={row.published ? "success" : "default"}
        label={row.published ? "Да" : "Нет"}
      />
    ),
  },
});
